import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { captureUtmParams } from './lib/tracking';
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import PageLoading from "./components/PageLoading";

const queryClient = new QueryClient();
const trackingEndpoint = import.meta.env.VITE_TRACKING_ENDPOINT as string | undefined;

const sendTrackingEvent = (payload: Record<string, unknown>) => {
  if (!trackingEndpoint) return;

  fetch(trackingEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => {});
};

const Index = lazy(() => import("./pages/Index"));
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Projects = lazy(() => import("./pages/Projects"));
const Blog = lazy(() => import("./pages/Blog"));
const Contact = lazy(() => import("./pages/Contact"));
const LocalLaw11 = lazy(() => import("./pages/LocalLaw11"));
const FISPGuide = lazy(() => import("./pages/FISPGuide"));
const NotFound = lazy(() => import("./pages/NotFound"));
const WhatIsLocalLaw11 = lazy(() => import("./pages/blog/WhatIsLocalLaw11"));
const FISPCycle10Deadlines = lazy(() => import("./pages/blog/FISPCycle10Deadlines"));
const ParapetInspectionSigns = lazy(() => import("./pages/blog/ParapetInspectionSigns"));
const FacadeInspectionCost = lazy(() => import("./pages/blog/FacadeInspectionCost"));
const ResolveDOBViolations = lazy(() => import("./pages/blog/ResolveDOBViolations"));
const WhenBuildingNeedsLeakInvestigation = lazy(() => import("./pages/blog/WhenBuildingNeedsLeakInvestigation"));
const DOBViolationCostNYC = lazy(() => import("./pages/blog/DOBViolationCostNYC"));
const FISPCondoCoopGuide = lazy(() => import("./pages/blog/FISPCondoCoopGuide"));
const ScaffoldPermitCostNYC = lazy(() => import("./pages/blog/ScaffoldPermitCostNYC"));
const FISPCycle10BoroughDeadlines = lazy(() => import("./pages/blog/FISPCycle10BoroughDeadlines"));
const AreasWeServe = lazy(() => import("./pages/AreasWeServe"));
const Manhattan = lazy(() => import("./pages/areas/Manhattan"));
const Brooklyn = lazy(() => import("./pages/areas/Brooklyn"));
const Queens = lazy(() => import("./pages/areas/Queens"));
const Bronx = lazy(() => import("./pages/areas/Bronx"));
const StatenIsland = lazy(() => import("./pages/areas/StatenIsland"));
const FacadeInspection = lazy(() => import("./pages/services/FacadeInspection"));
const ParapetInspection = lazy(() => import("./pages/services/ParapetInspection"));
const LeakInvestigation = lazy(() => import("./pages/services/LeakInvestigation"));
const StructuralRepairs = lazy(() => import("./pages/services/StructuralRepairs"));
const DOBViolations = lazy(() => import("./pages/services/DOBViolations"));
const EngineeringReports = lazy(() => import("./pages/services/EngineeringReports"));
const ConstructionConsulting = lazy(() => import("./pages/services/ConstructionConsulting"));
const ContractTemplates = lazy(() => import("./pages/services/ContractTemplates"));
const BuildingReport = lazy(() => import("./pages/BuildingReport"));
const BuildingReportResults = lazy(() => import("./pages/BuildingReportResults"));
const Resources = lazy(() => import("./pages/Resources"));
const AIAContractsGuide = lazy(() => import("./pages/resources/AIAContractsGuide"));
const ConstructionContractsGuide = lazy(() => import("./pages/resources/ConstructionContractsGuide"));
const ConstructionContractEssentials = lazy(() => import("./pages/blog/ConstructionContractEssentials"));
const LocalLaw11FacadeInspectionNYC = lazy(() => import("./pages/blog/LocalLaw11FacadeInspectionNYC"));
const UniversityPlace = lazy(() => import("./pages/case-studies/UniversityPlace"));
const HenryStreet = lazy(() => import("./pages/case-studies/HenryStreet"));
const RiversideDrive = lazy(() => import("./pages/case-studies/RiversideDrive"));
const ViolationCalculator = lazy(() => import("./pages/tools/ViolationCalculator"));
const Products = lazy(() => import("./pages/Products"));
const FacadeInspectionLP = lazy(() => import("./pages/lp/FacadeInspectionLP"));
const DOBViolationsLP = lazy(() => import("./pages/lp/DOBViolationsLP"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const ChatBot = lazy(() => import("./components/ChatBot"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PageTracker() {
  const { pathname } = useLocation();

  // Capture UTM/gclid/fbclid params on first load
  useEffect(() => {
    captureUtmParams();
  }, []);

  // Track page views on route change
  useEffect(() => {
    sendTrackingEvent({
      type: 'pageview',
      page: pathname,
      referrer: document.referrer,
      title: document.title,
    });
  }, [pathname]);

  // Track interactions (clicks on CTAs, phone, email, forms, chat)
  useEffect(() => {
    const trackClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement).closest('a, button');
      if (!el) return;

      let action = '';
      let label = '';

      const href = el.getAttribute('href') || '';
      const text = (el.textContent || '').trim().substring(0, 80);

      if (href.startsWith('tel:')) {
        action = 'click_phone';
        label = href.replace('tel:', '');
      } else if (href.startsWith('mailto:')) {
        action = 'click_email';
        label = href.replace('mailto:', '');
      } else if (text.match(/schedule|assessment|contact|get started|call/i)) {
        action = 'click_cta';
        label = text;
      } else if (text.match(/download|pdf|report/i)) {
        action = 'click_download';
        label = text;
      } else if (text.match(/submit|send/i) || el.getAttribute('type') === 'submit') {
        action = 'form_submit';
        label = text || 'Form submit';
      } else {
        return; // Don't track regular navigation clicks
      }

      sendTrackingEvent({
        type: 'event',
        page: window.location.pathname,
        action,
        label,
      });
    };

    document.addEventListener('click', trackClick);
    return () => document.removeEventListener('click', trackClick);
  }, []);

  return null;
}

function LanguageSync() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const firstSegment = pathname.split('/').filter(Boolean)[0];
    const lang = firstSegment === 'es' ? 'es' : 'en';
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [pathname, i18n]);

  return null;
}

function DeferredChatBot() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const show = () => setEnabled(true);
    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(show, { timeout: 3000 });
      return () => window.cancelIdleCallback(idleId);
    }
    const timer = window.setTimeout(show, 3000);
    return () => window.clearTimeout(timer);
  }, []);

  if (!enabled) return null;
  return (
    <Suspense fallback={null}>
      <ChatBot />
    </Suspense>
  );
}

const routes = [
  { path: "/", element: <Index /> },
  { path: "/about", element: <About /> },
  { path: "/services", element: <Services /> },
  { path: "/services/facade-inspection", element: <FacadeInspection /> },
  { path: "/services/parapet-inspection", element: <ParapetInspection /> },
  { path: "/services/leak-investigation", element: <LeakInvestigation /> },
  { path: "/services/structural-repairs", element: <StructuralRepairs /> },
  { path: "/services/dob-violations", element: <DOBViolations /> },
  { path: "/services/engineering-reports", element: <EngineeringReports /> },
  { path: "/services/construction-consulting", element: <ConstructionConsulting /> },
  { path: "/services/contract-templates", element: <ContractTemplates /> },
  { path: "/projects", element: <Projects /> },
  { path: "/blog", element: <Blog /> },
  { path: "/blog/what-is-local-law-11", element: <WhatIsLocalLaw11 /> },
  { path: "/blog/fisp-cycle-10-deadlines", element: <FISPCycle10Deadlines /> },
  { path: "/blog/parapet-inspection-signs", element: <ParapetInspectionSigns /> },
  { path: "/blog/facade-inspection-cost-nyc", element: <FacadeInspectionCost /> },
  { path: "/blog/resolve-dob-violations", element: <ResolveDOBViolations /> },
  { path: "/blog/when-building-needs-leak-investigation", element: <WhenBuildingNeedsLeakInvestigation /> },
  { path: "/blog/dob-violation-cost-nyc", element: <DOBViolationCostNYC /> },
  { path: "/blog/fisp-condo-coop-guide", element: <FISPCondoCoopGuide /> },
  { path: "/blog/construction-contract-essentials", element: <ConstructionContractEssentials /> },
  { path: "/blog/scaffold-permit-cost-nyc", element: <ScaffoldPermitCostNYC /> },
  { path: "/blog/fisp-cycle-10-borough-deadlines", element: <FISPCycle10BoroughDeadlines /> },
  { path: "/blog/local-law-11-facade-inspection-nyc", element: <LocalLaw11FacadeInspectionNYC /> },
  { path: "/areas-we-serve", element: <AreasWeServe /> },
  { path: "/areas/manhattan", element: <Manhattan /> },
  { path: "/areas/brooklyn", element: <Brooklyn /> },
  { path: "/areas/queens", element: <Queens /> },
  { path: "/areas/bronx", element: <Bronx /> },
  { path: "/areas/staten-island", element: <StatenIsland /> },
  { path: "/contact", element: <Contact /> },
  { path: "/local-law-11", element: <LocalLaw11 /> },
  { path: "/fisp-guide", element: <FISPGuide /> },
  { path: "/building-report", element: <BuildingReport /> },
  { path: "/building-report/results", element: <BuildingReportResults /> },
  { path: "/resources", element: <Resources /> },
  { path: "/resources/aia-contracts-guide", element: <AIAContractsGuide /> },
  { path: "/resources/construction-contracts-guide", element: <ConstructionContractsGuide /> },
  { path: "/case-studies/1-university-place", element: <UniversityPlace /> },
  { path: "/case-studies/100-henry-street", element: <HenryStreet /> },
  { path: "/case-studies/765-riverside-drive", element: <RiversideDrive /> },
  { path: "/tools/violation-calculator", element: <ViolationCalculator /> },
  { path: "/products", element: <Products /> },
  { path: "/lp/facade-inspection", element: <FacadeInspectionLP /> },
  { path: "/lp/dob-violations", element: <DOBViolationsLP /> },
  { path: "/privacy", element: <PrivacyPolicy /> },
  { path: "/terms", element: <TermsOfService /> },
];

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <PageTracker />
        <LanguageSync />
        <RouteErrorBoundary>
          <Suspense fallback={<PageLoading />}>
            <Routes>
              {/* English routes */}
              {routes.map((r) => (
                <Route key={r.path} path={r.path} element={r.element} />
              ))}
              {/* Spanish routes — same components, /es prefix */}
              {routes.map((r) => (
                <Route key={`es-${r.path}`} path={`/es${r.path}`} element={r.element} />
              ))}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
        <DeferredChatBot />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
export const BUILD_VERSION = "2.2.0-20260722";
